/**
 * Workspace selection logic — pure function so it's easy to test.
 *
 * Given the user's role name and the set of workspaces visible to them, pick
 * the one that should activate. Rules:
 *
 *   1. Among workspaces bound to the user's role (case-insensitive match) and
 *      flagged `isDefault`, pick the most recently updated one.
 *   2. If none match, return null — the console renders its unscoped layout.
 *
 * Suspension (the "View full console" toggle) is layered on top in the
 * provider — this helper doesn't know about it.
 */
import type { WorkspaceView } from "@/workspace/types";

export function pickWorkspaceForRole(
  roleName: string | null | undefined,
  workspaces: WorkspaceView[],
): WorkspaceView | null {
  if (!roleName) return null;
  const target = roleName.trim().toLowerCase();
  if (!target) return null;
  const matches = workspaces.filter(
    (w) => w.isDefault && w.roleName && w.roleName.trim().toLowerCase() === target,
  );
  if (matches.length === 0) return null;
  // Most recently updated wins (defensive — DB unique constraint should make
  // this a singleton).
  matches.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  return matches[0];
}
